import React from "react";
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NamingConventionBuilder, type NamingConventionValue } from "@/components/shared/NamingConventionBuilder";
import type { AdAccountSettings } from "@/hooks/useOnboardingSettings";

interface StepNamingConventionProps {
  adAccountId: string;
  settings: AdAccountSettings | null;
  onSave: (data: Partial<AdAccountSettings>) => Promise<any>;
  onValidChange: (valid: boolean) => void;
}

const DEFAULT_VALUE: NamingConventionValue = {
  tags: [],
  separator: "_",
  custom_separator: "",
  custom_tags: [],
};

export function StepNamingConvention({
  adAccountId,
  settings,
  onSave,
  onValidChange,
}: StepNamingConventionProps) {
  const existingConvention = settings?.naming_convention as NamingConventionValue | null;

  const [value, setValue] = useState<NamingConventionValue>(
    existingConvention
      ? {
          tags: existingConvention.tags ?? [],
          separator: existingConvention.separator ?? "_",
          custom_separator: existingConvention.custom_separator ?? "",
          custom_tags: existingConvention.custom_tags ?? [],
        }
      : DEFAULT_VALUE,
  );

  // Sync from loaded settings
  useEffect(() => {
    if (existingConvention) {
      setValue({
        tags: existingConvention.tags ?? [],
        separator: existingConvention.separator ?? "_",
        custom_separator: existingConvention.custom_separator ?? "",
        custom_tags: existingConvention.custom_tags ?? [],
      });
    }
  }, [settings?.naming_convention]);

  // Always valid (optional step)
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Auto-save on changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      onSave({
        naming_convention: value as any,
        step_naming_completed: true,
      });
    }, 500);
    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ad Naming Convention</CardTitle>
        <CardDescription>
          Build a naming pattern for your ads. This helps keep your campaigns organized.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <NamingConventionBuilder value={value} onChange={setValue} />
      </CardContent>
    </Card>
  );
}
